/**
 * Print one arrangement the console is still deciding about.
 *
 * `test-print.ts` proves the **printer**, from this shop's **saved** receipt and
 * a fixed sample. This proves the **arrangement**, from a draft nobody has saved
 * — because the moment somebody wants paper in their hand is the moment before
 * they commit a document to two thousand shops, and saving first to look at it
 * is exactly the thing that cannot be undone quickly.
 *
 * ── Three things it deliberately does not touch ───────────────────────────
 *
 * **The held template.** `templateInHand()` is what this kiosk prints for real
 * customers, and a proof that replaced it would leave a shop printing a draft
 * somebody abandoned. `printSlip` takes a built `Slip`, so nothing here has to
 * go near the cache.
 *
 * **The shop's own record.** The slip is drawn against the *sample* shop rather
 * than this one, so the operator gets the paper their preview promised. That is
 * the opposite of `runTestPrint`'s choice, and on purpose: a test print proves
 * *this machine*, where the real shop name is the point; a proof answers «is my
 * arrangement right», where a name the console did not draw is a difference the
 * reader has to explain away.
 *
 * **Whether it worked.** The server is told either way. A printer that refused
 * is exactly what the operator pressed the button to find out, and a feature
 * that reports only its successes cannot diagnose anything.
 */
import { buildSlip, sampleSale } from '@mgc/receipt'
import { SAMPLE_BRANCH } from '@mgc/receipt'
import { trpc } from '@/api/client'
import { printSlip } from '@/shell/print'
import { record } from './diagnostics'

/**
 * Claim it, draw it, print it, say what happened.
 *
 * Never throws: it is called from a socket handler, and an unhandled rejection
 * on the page a customer is standing at is a worse outcome than a slip that did
 * not come out. Everything it knows ends up in the diagnostic ring and — the
 * part the console reads — on the proof's own row.
 */
export async function runReceiptProof(proofId: string): Promise<void> {
  let taken: { template: unknown; sample: string }
  try {
    /*
     * The fetch is the claim. Two sockets for one kiosk — a reconnect race, a
     * second tab — would otherwise both print, and a wasted slip is the one
     * outcome a person standing at the printer definitely notices. The loser
     * gets a refusal here rather than a second piece of paper.
     */
    const answer = await trpc.receiptTemplate.proof.query({ proofId })
    taken = { template: answer.template, sample: answer.sample }
  } catch (error) {
    record('warn', `receipt proof ${proofId} could not be claimed: ${String(error)}`)
    return
  }

  const sale = sampleSale(taken.sample)

  try {
    const slip = buildSlip(sale.order, SAMPLE_BRANCH, taken.template as Parameters<typeof buildSlip>[2])
    const outcome = await printSlip(slip)
    const detail = outcome.via === 'thermal' ? `thermal (${outcome.mode})` : `browser (${outcome.because})`
    record('info', `receipt proof printed: ${detail}`)
    await tell(proofId, outcome.via, detail)
  } catch (error) {
    const why = String(error)
    record('error', `receipt proof failed to print: ${why}`)
    await tell(proofId, 'failed', why.slice(0, 200))
  }
}

/**
 * Report back, and swallow the reporting's own failure.
 *
 * A kiosk that printed the paper and could not say so has still printed the
 * paper — the operator is looking at a printer, not at this call — and throwing
 * here would turn a successful proof into an error in the log.
 */
async function tell(proofId: string, outcome: 'thermal' | 'browser' | 'failed', detail: string): Promise<void> {
  try {
    await trpc.receiptTemplate.proofPrinted.mutate({ proofId, outcome, detail })
  } catch (error) {
    record('warn', `receipt proof ${proofId} printed but could not be reported: ${String(error)}`)
  }
}
